import { Helmet } from "react-helmet-async";
import React, { useContext } from "react";
import { MetadataContext } from "../Contexts/MetadataContext";
import { useLocation } from "react-router-dom";
import { getPageTypeId } from "../Utils/pageMap";

export default function Meta() {
  const { metadata, dynamicMeta, loading } = useContext(MetadataContext);
  const location = useLocation();

  if (loading) return null;

  // 🔥 PRIORITY:
  // 1. Product / Category (dynamicMeta)
  // 2. Static pages (MetaData table)
  // 3. Fallback

  let meta = dynamicMeta;

  if (!meta) {
    const pageTypeId = getPageTypeId(location.pathname);
    const metaArray = metadata ? Object.values(metadata) : [];

    meta =
      metaArray.find(m => m.Page === pageTypeId) || {
        Title: "Home Depot of lebanon | Tiles, Ceramics and sanitary ware",
        Description:
          "Discover premium tiles, ceramics, bathroom sinks, and sanitary ware at Home Depot Lebanon, offering stylish designs, top quality, and reliable solutions for every space.",
        Keywords:
          "tiles, ceramics, sanitary ware, bathroom sinks, home finishing",
        Url: `${window.location.origin}${location.pathname}`,
        Type: "website",
        Image: `${window.location.origin}/images/og-default.jpg`,
        TwitterCard: "summary_large_image",
      };
  }

  // JSON-LD structured data. On product pages emit a Product schema so
  // search engines and AI crawlers can identify the item without guessing.
  // Price is intentionally omitted (catalog is "call for price"), which
  // keeps the Offer valid instead of failing validation with a text price.
  const jsonLd =
    meta.Type === "product"
      ? {
          "@context": "https://schema.org/",
          "@type": "Product",
          name: meta.Title,
          description: meta.Description,
          ...(meta.Image ? { image: meta.Image } : {}),
          ...(meta.Url ? { url: meta.Url } : {}),
          offers: {
            "@type": "Offer",
            ...(meta.Url ? { url: meta.Url } : {}),
            availability: "https://schema.org/InStock",
            areaServed: "Lebanon",
            seller: { "@type": "Organization", name: "Home Depot of Lebanon" },
          },
        }
      : {
          "@context": "https://schema.org/",
          "@type": "Organization",
          name: "Home Depot of Lebanon",
          url: typeof window !== "undefined" ? window.location.origin : "",
        };

  return (
  <Helmet>
    <title>{meta.Title}</title>
    <meta name="description" content={meta.Description} />
    <meta name="keywords" content={meta.Keywords} />
    <meta name="robots" content="index, follow" />

    <link rel="canonical" href={meta.Url} />

    {/* Open Graph */}
    <meta property="og:title" content={meta.Title} />
    <meta property="og:description" content={meta.Description} />
    <meta property="og:type" content={meta.Type} />
    <meta property="og:url" content={meta.Url} />
    <meta property="og:image" content={meta.Image} />

    {/* Twitter */}
    <meta name="twitter:card" content={meta.TwitterCard} />
    <meta name="twitter:title" content={meta.Title} />
    <meta name="twitter:description" content={meta.Description} />
    <meta name="twitter:image" content={meta.Image} />

    {/* Structured data (JSON-LD) */}
    <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
  </Helmet>
);
}
